// src/pages/NieuweAnalysePage.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { Navigate, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { videoHeaders, isVerificatieFout, meldVerificatieVerlopen } from '../utils/videoVerificatie';
import { uploadVideo, opnieuwTekst } from '../utils/videoUpload';
import VideoPrivacyInfo from '../components/VideoPrivacyInfo';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

const ALLOWED_EXTENSIONS = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
  'video/x-msvideo': ['.avi'],
};
const ALLOWED_MIME_TYPES = Object.keys(ALLOWED_EXTENSIONS);

function getMimeType(file) {
  if (file.type && ALLOWED_MIME_TYPES.includes(file.type)) return file.type;
  const ext = file.name.toLowerCase().split('.').pop();
  const extMap = { mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', avi: 'video/x-msvideo' };
  return extMap[ext] || file.type;
}

function getDoorloopjeIcon(d) {
  return d.thumbnail?.formats?.thumbnail?.url || d.thumbnail?.url;
}

function NieuweAnalysePage() {
  const { doorloopjes, currentUser } = useDoorloopjes();
  const navigate = useNavigate();
  // "Opnieuw proberen" in de lijst: een poging die niet aankwam, met haar keuzes.
  const [searchParams] = useSearchParams();
  const hervat = searchParams.get('hervat');
  const [hervatBestand, setHervatBestand] = useState(null);

  const [selectedDoorloopje, setSelectedDoorloopje] = useState(null);  // documentId
  const [doorloopjeSearch, setDoorloopjeSearch] = useState('');
  const [isDoorloopjeOpen, setIsDoorloopjeOpen] = useState(false);
  const doorloopjeRef = useRef(null);

  const [extraContext, setExtraContext] = useState('');
  // Keuze van de leerkracht: gezichten onherkenbaar in de kopie die naar de AI gaat (standaard uit,
  // omdat het de feedback over reacties van leerlingen beperkt).
  const [blurGezichten, setBlurGezichten] = useState(false);
  const [alleenAudio, setAlleenAudio] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState(null); // null = all, array = specific step numbers
  const [analyseMode, setAnalyseMode] = useState('all'); // 'all' or 'custom'
  const [steps, setSteps] = useState([]); // steps of the chosen doorloopje; the list does not carry them
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadEta, setUploadEta] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [onderbroken, setOnderbroken] = useState(false);
  const [storing, setStoring] = useState(false);
  const xhrRef = useRef(null);
  const uploadStartRef = useRef(null);

  // Bij "Opnieuw proberen": dezelfde keuzes als de poging die niet aankwam.
  useEffect(() => {
    if (!hervat) return;
    axios
      .get(`${STRAPI_URL}/api/video-analyses/${hervat}?populate[doorloopje][fields][0]=documentId`,
        { headers: videoHeaders(localStorage.getItem('jwt')) })
      .then(res => {
        const a = res.data.data;
        if (a.status !== 'uploading') return;
        setHervatBestand({ naam: a.original_filename, hervatbaar: a.hervatbaar });
        if (a.doorloopje?.documentId) setSelectedDoorloopje(a.doorloopje.documentId);
        setExtraContext(a.extra_context || '');
        setBlurGezichten(!!a.blur_gezichten);
        setAlleenAudio(!!a.alleen_audio);
        if (a.selected_steps?.length) { setAnalyseMode('custom'); setSelectedSteps(a.selected_steps); }
      })
      .catch(() => {});
  }, [hervat]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (doorloopjeRef.current && !doorloopjeRef.current.contains(e.target)) {
        setIsDoorloopjeOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch the chosen doorloopje's steps (the list leaves them out to stay small)
  useEffect(() => {
    setSteps([]);
    if (!selectedDoorloopje) return;
    let cancelled = false;
    axios
      .get(
        `${STRAPI_URL}/api/doorloopjes/${selectedDoorloopje}` +
          `?populate[steps][fields][0]=title` +
          `&populate[steps][populate][image][fields][0]=url&populate[steps][populate][image][fields][1]=formats`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` } }
      )
      .then(res => { if (!cancelled) setSteps(res.data.data.steps || []); })
      .catch(err => console.error('Stappen van het doorloopje ophalen mislukt:', err));
    return () => { cancelled = true; };
  }, [selectedDoorloopje]);

  // beforeunload warning during upload
  useEffect(() => {
    if (!isUploading) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isUploading]);

  const formatEta = useCallback((seconds) => {
    if (seconds == null || !isFinite(seconds) || seconds <= 0) return null;
    const s = Math.ceil(seconds);
    if (s < 60) return `nog ~${s} seconden`;
    const m = Math.ceil(s / 60);
    return m === 1 ? 'nog ~1 minuut' : `nog ~${m} minuten`;
  }, []);

  if (!currentUser?.has_video_analysis) {
    return <Navigate to="/dashboard" replace />;
  }

  const selectedDoorloopjeObj = doorloopjes.find(d => d.documentId === selectedDoorloopje);

  // Doorloopjes alleen voor videocoaching (zoals "Film je eigen les") zijn uitleg, geen lens.
  const lenzen = doorloopjes.filter(d => !d.alleen_videocoaching);
  const filteredDoorloopjes = doorloopjeSearch.trim()
    ? lenzen.filter(d => d.title.toLowerCase().includes(doorloopjeSearch.toLowerCase()))
    : lenzen;

  function handleSelectDoorloopje(d) {
    setSelectedDoorloopje(d.documentId);
    setDoorloopjeSearch('');
    setIsDoorloopjeOpen(false);
    setAnalyseMode('all');
    setSelectedSteps(null);
  }

  function handleClearDoorloopje() {
    setSelectedDoorloopje(null);
    setDoorloopjeSearch('');
    setAnalyseMode('all');
    setSelectedSteps(null);
  }

  function validateAndSetFile(file) {
    setFileError(null);
    setSelectedFile(null);
    if (!file) return;

    const mime = getMimeType(file);
    if (!ALLOWED_MIME_TYPES.includes(mime)) {
      setFileError('Ongeldig bestandsformaat. Toegestaan: MP4, MOV, WebM, AVI.');
      return;
    }
    setSelectedFile(file);
  }

  function handleFileSelect(e) {
    validateAndSetFile(e.target.files?.[0]);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragOver(false);
    if (isUploading) return;
    validateAndSetFile(e.dataTransfer.files?.[0]);
  }

  function handleDragOver(e) {
    e.preventDefault();
    if (!isUploading) setIsDragOver(true);
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedDoorloopje || !selectedFile || fileError) return;

    setIsUploading(true);
    setUploadError(null);

    const jwt = localStorage.getItem('jwt');
    const mime = getMimeType(selectedFile);
    const totaal = selectedFile.size;
    let startBytes = null;
    try {
      await uploadVideo({
        bestand: selectedFile,
        mime,
        headers: videoHeaders(jwt),
        xhrRef,
        // Gaat verder met een eerdere poging van hetzelfde bestand, als die er is.
        start: async () => (await axios.post(
          `${STRAPI_URL}/api/video-analyses/upload-url`,
          {
            doorloopje_id: selectedDoorloopje,
            filename: selectedFile.name,
            mime_type: mime,
            file_size_bytes: totaal,
            extra_context: extraContext || undefined,
            selected_steps: analyseMode === 'custom' && selectedSteps ? selectedSteps : undefined,
            blur_gezichten: blurGezichten && !alleenAudio,
            alleen_audio: alleenAudio,
          },
          { headers: videoHeaders(jwt) }
        )).data.data,
        opStoring: setStoring,
        opVoortgang: (bytes) => {
          if (startBytes === null) {
            startBytes = bytes;
            uploadStartRef.current = Date.now();
          }
          setUploadProgress(Math.floor((bytes / totaal) * 100));
          const elapsed = (Date.now() - uploadStartRef.current) / 1000;
          if (elapsed > 1 && bytes > startBytes) {
            setUploadEta((totaal - bytes) / ((bytes - startBytes) / elapsed));
          }
        },
      });

      navigate('/video-analyse');

    } catch (err) {
      if (isVerificatieFout(err)) return meldVerificatieVerlopen();
      console.error('Upload error:', err);
      setStoring(false);
      const melding = err.response?.data?.error?.message;
      setOnderbroken(!melding);
      setUploadError(
        melding ||
        'De verbinding viel weg. Wat al binnen is, blijft bewaard. Klik op "Verder uploaden" om verder te gaan.'
      );
      setIsUploading(false);
    }
  }

  return (
    <div className="page-container video-analyse-page">
      <div className="page-introduction">
        <nav className="breadcrumbs">
          <Link to="/video-analyse">Video Analyse</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Nieuwe analyse</span>
        </nav>
        <div className="page-header-row">
          <h1>Nieuwe analyse</h1>
          <VideoPrivacyInfo />
        </div>
        <p>Upload een video van je les en kies een doorloopje als lens. De AI bekijkt je video. Per stap van het doorloopje hoor je wat goed gaat en wat beter kan.</p>
      </div>
      <main className="video-analyse-content">
        <form className="analyse-form" onSubmit={handleSubmit}>

          {/* Doorloopje search picker */}
          <div className="analyse-form-field">
            <label>Doorloopje</label>
            <p className="field-hint">De lens waarmee de AI naar je les kijkt.</p>

            {selectedDoorloopjeObj ? (
              <div className="doorloopje-selected">
                {getDoorloopjeIcon(selectedDoorloopjeObj) && (
                  <img src={getDoorloopjeIcon(selectedDoorloopjeObj)} alt="" className="doorloopje-selected-img" />
                )}
                <span className="doorloopje-selected-title">{selectedDoorloopjeObj.title}</span>
                {!isUploading && (
                  <button type="button" className="doorloopje-selected-clear" onClick={handleClearDoorloopje} title="Wijzig doorloopje">
                    &#x2715;
                  </button>
                )}
              </div>
            ) : (
              <div className="doorloopje-search-wrapper" ref={doorloopjeRef}>
                <input
                  type="text"
                  className="doorloopje-search-input"
                  aria-label="Zoek een doorloopje"
                  placeholder="Zoek een doorloopje..."
                  value={doorloopjeSearch}
                  onChange={(e) => { setDoorloopjeSearch(e.target.value); setIsDoorloopjeOpen(true); }}
                  onFocus={() => setIsDoorloopjeOpen(true)}
                  disabled={isUploading}
                />
                {isDoorloopjeOpen && (
                  <div className="doorloopje-dropdown">
                    {filteredDoorloopjes.length > 0 ? (
                      filteredDoorloopjes.map((d) => {
                        const iconUrl = getDoorloopjeIcon(d);
                        return (
                          <button
                            type="button"
                            key={d.documentId}
                            className="doorloopje-dropdown-item"
                            onClick={() => handleSelectDoorloopje(d)}
                          >
                            {iconUrl ? (
                              <img src={iconUrl} alt="" className="doorloopje-dropdown-img" />
                            ) : (
                              <div className="doorloopje-dropdown-img placeholder" />
                            )}
                            <span className="doorloopje-dropdown-title">{d.title}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="doorloopje-dropdown-empty">Geen doorloopjes gevonden</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step selection (only when a doorloopje is selected) */}
          {selectedDoorloopjeObj && steps.length > 0 && (
            <div className="analyse-form-field">
              <label>Welke stappen analyseren?</label>
              <p className="field-hint">Kies alle stappen of een paar.</p>
              <div className="step-mode-toggle">
                <button
                  type="button"
                  className={`step-mode-btn${analyseMode === 'all' ? ' active' : ''}`}
                  onClick={() => { setAnalyseMode('all'); setSelectedSteps(null); }}
                  disabled={isUploading}
                >
                  Alle stappen
                </button>
                <button
                  type="button"
                  className={`step-mode-btn${analyseMode === 'custom' ? ' active' : ''}`}
                  onClick={() => {
                    setAnalyseMode('custom');
                    setSelectedSteps(steps.map((_, i) => i + 1));
                  }}
                  disabled={isUploading}
                >
                  Specifieke stappen
                </button>
              </div>
              {analyseMode === 'custom' && (
                <div className="step-checkboxes">
                  {steps.map((step, i) => {
                    const stepNum = i + 1;
                    const isChecked = selectedSteps?.includes(stepNum);
                    const stepImg = step.image?.formats?.thumbnail?.url || step.image?.url;
                    return (
                      <label key={stepNum} className={`step-checkbox-item${isChecked ? ' checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            setSelectedSteps(prev => {
                              if (!prev) return [stepNum];
                              return isChecked
                                ? prev.filter(n => n !== stepNum)
                                : [...prev, stepNum].sort((a, b) => a - b);
                            });
                          }}
                          disabled={isUploading}
                        />
                        {stepImg ? (
                          <img src={stepImg} alt="" className="step-checkbox-img" />
                        ) : (
                          <span className="step-checkbox-number">{stepNum}</span>
                        )}
                        <span className="step-checkbox-title">{step.title}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* File picker with drag & drop */}
          <div className="analyse-form-field">
            <label htmlFor="video-file">Video</label>
            {hervatBestand && (
              <p className="field-hint">Het bestand was {hervatBestand.naam}. {opnieuwTekst(hervatBestand.hervatbaar)}</p>
            )}
            <div
              className={`drop-zone${isDragOver ? ' drop-zone--active' : ''}${isUploading ? ' drop-zone--disabled' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isUploading && document.getElementById('video-file').click()}
            >
              <input
                type="file"
                id="video-file"
                accept=".mp4,.mov,.webm,.avi"
                onChange={handleFileSelect}
                disabled={isUploading}
                style={{ display: 'none' }}
              />
              {selectedFile && !fileError ? (
                <p className="drop-zone-file">{selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(0)} MB)</p>
              ) : (
                <p className="drop-zone-hint">
                  Sleep een video hierheen of <span className="drop-zone-link">kies een bestand</span>
                  <br /><span className="drop-zone-formats">MP4, MOV, WebM, AVI</span>
                </p>
              )}
            </div>
            {fileError && <p className="field-error">{fileError}</p>}
          </div>

          {/* Extra context textarea */}
          <div className="analyse-form-field">
            <label htmlFor="extra-context">Extra context</label>
            <p className="field-hint" id="extra-context-hint">Mag je leeg laten. Bijvoorbeeld je lesdoel of een vraag voor de AI. Noem geen namen van leerlingen.</p>
            <textarea
              id="extra-context"
              aria-describedby="extra-context-hint"
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              placeholder="Bijvoorbeeld: ik wil weten of ik lang genoeg wacht na een vraag."
              maxLength={2000}
              rows={3}
              disabled={isUploading}
            />
            <span className="char-count">{extraContext.length}/2000</span>
          </div>

          {/* Gezichten blurren voor de AI (optioneel) */}
          <div className="analyse-form-field">
            <label className={`blur-optie${blurGezichten && !alleenAudio ? ' checked' : ''}${alleenAudio ? ' uitgeschakeld' : ''}`}>
              <input
                type="checkbox"
                id="blur-gezichten"
                checked={blurGezichten && !alleenAudio}
                onChange={(e) => setBlurGezichten(e.target.checked)}
                disabled={isUploading || alleenAudio}
              />
              <span>
                <strong>Gezichten onherkenbaar maken voor de AI-analyse</strong>
                <span className="blur-optie-uitleg">
                  De AI krijgt dan een kopie met vage gezichten, ook dat van jou. Je eigen video blijft scherp. De AI ziet dan minder van hoe leerlingen reageren. Over hun reacties krijg je dus minder feedback.
                </span>
              </span>
            </label>
          </div>

          {/* Alleen het geluid naar de AI (optioneel) */}
          <div className="analyse-form-field">
            <label className={`blur-optie${alleenAudio ? ' checked' : ''}`}>
              <input
                type="checkbox"
                id="alleen-audio"
                checked={alleenAudio}
                onChange={(e) => setAlleenAudio(e.target.checked)}
                disabled={isUploading}
              />
              <span>
                <strong>Alleen het geluid laten analyseren</strong>
                <span className="blur-optie-uitleg">
                  We bewaren dan alleen het geluid van je les. We verwijderen de video direct en de AI krijgt geen beeld. Je luistert je les terug in plaats van hem te bekijken. De feedback gaat over wat er gezegd wordt. Wat je alleen kunt zien, kan de AI niet beoordelen. Denk aan voordoen, door de klas lopen of wat leerlingen doen.
                </span>
              </span>
            </label>
          </div>

          {/* Progress bar with ETA */}
          {isUploading && (
            <div className="upload-progress-section">
              <div className="upload-status-row">
                <p className="upload-status-text">Video uploaden... {uploadProgress}%</p>
                {!storing && formatEta(uploadEta) && (
                  <p className="upload-eta">{formatEta(uploadEta)}</p>
                )}
              </div>
              <div className="upload-progress-bar">
                <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="upload-hint">
                {storing
                  ? 'De verbinding is weg. Wat al binnen is, blijft bewaard. Het uploaden gaat vanzelf verder als de verbinding terug is.'
                  : 'Sluit deze pagina niet tijdens het uploaden.'}
              </p>
            </div>
          )}

          {/* Upload error */}
          {uploadError && <p className="field-error upload-error">{uploadError}</p>}

          {/* Submit button */}
          <button
            type="submit"
            className="btn-primary-action"
            disabled={!selectedDoorloopje || !selectedFile || !!fileError || isUploading}
          >
            {isUploading ? 'Uploaden...' : onderbroken ? 'Verder uploaden' : 'Analyse starten'}
          </button>

        </form>
      </main>
    </div>
  );
}

export default NieuweAnalysePage;
